import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { featureFlags } from '../../config/featureFlags';
import { useAuth } from '../../contexts/AuthContext';
import { BottomChromeProvider } from '../../contexts/BottomChromeContext';
import { useChat } from '../../contexts/ChatContext';
import { PhoneTripSearchProvider } from '../../contexts/PhoneTripSearchContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SIZING } from '../../styles/tokens';
import CartDropdown from '../cart/CartDropdown';
import ConciergeFab from '../chatbot/ConciergeFab';
import ConciergePanel from '../chatbot/ConciergePanel';
import MobileConciergeSheet from '../chatbot/MobileConciergeSheet';
import ConsentBanner from '../consent/ConsentBanner';
import CookiePreferencesPanel from '../consent/CookiePreferencesPanel';
import MarketingEmailPrompt from '../consent/MarketingEmailPrompt';
import Footer from './Footer';
import Header from './Header';
import MobileBottomNav from './MobileBottomNav';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { isChatOpen } = useChat();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isCheckoutPage = location.pathname.startsWith('/checkout');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const showChat = featureFlags.chatbot && !isAdminPage && !isMobile;
  const sidebarOffset = showChat && isChatOpen ? '380px' : '0px';

  return (
    <BottomChromeProvider>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          paddingRight: sidebarOffset,
          transition: 'padding-right 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <PhoneTripSearchProvider>
          <Header />
          <main
            style={{
              flex: 1,
              paddingTop: isMobile ? SIZING.mobileAppBarHeight : '72px',
              paddingBottom: isMobile && isAdminPage ? SIZING.bottomNavOffset : undefined,
            }}
          >
            {children}
          </main>
        </PhoneTripSearchProvider>
        {/* Site-wide footer — suppressed on admin pages. Self-handles its own
          mobile bottom-nav clearance, so `<main>` above no longer reserves
          that padding except on admin routes (where the footer doesn't
          render to absorb it). */}
        {!isAdminPage && <Footer />}
        <CartDropdown hideIcon={true} />

        {/* Mobile bottom tab bar — self-gates on mobile. Suppressed on the
          checkout flow: the fixed bar otherwise occludes the lower payment
          fields, and offering tab navigation mid-payment invites abandonment. */}
        {!isCheckoutPage && <MobileBottomNav />}

        <CookiePreferencesPanel />

        {/* Cookie consent banner (fixed overlay) plus the in-flow spacer that lets the
          page scroll clear of it. After the footer so it follows the page in tab order. */}
        <ConsentBanner />
        <MarketingEmailPrompt />

        {/* Concierge — desktop sidebar on desktop; FAB + bottom sheet on mobile.
          Gated off by featureFlags.chatbot (implementation being rebuilt, not
          in first release). Suppressed on admin pages; the mobile FAB also
          requires a signed-in user (the concierge chat is an authenticated
          feature). */}
        {featureFlags.chatbot && !isAdminPage && !isMobile && <ConciergePanel />}
        {featureFlags.chatbot && !isAdminPage && isMobile && isAuthenticated && (
          <>
            <ConciergeFab />
            <MobileConciergeSheet />
          </>
        )}
      </div>
    </BottomChromeProvider>
  );
};

export default Layout;
